import type { DataSourceAbstract, RichTextContentProtocol, RichTextOfTextNode, VariableADTvalue } from '@lighthouse/core'
import { DEVICE_ID_VALUE, VariableType } from '@lighthouse/core'
import { variableSystemNameMap, type VariableTree2 } from '@lighthouse/shared'
import { makeMatchPI } from 'ts-adt/MakeADT'

interface VariableTagProps {
    data?: VariableADTvalue
    pageOptions?: VariableTree2[]
    upstreamOptions?: VariableTree2[]
    userOption?: VariableTree2
    dataSourceList: DataSourceAbstract[]
}

export const textRichVariableName = ({ data, pageOptions, upstreamOptions, userOption, dataSourceList }: Partial<VariableTagProps> = {}):
    | string
    | undefined => {
    if (!data) {
        return
    }

    return makeMatchPI('type')(data)(
        {
            [VariableType.PAGE_LINK]: v => {
                return v.pageLinkVariable?.value === 'CURRENT_PAGE' ? '{当前页面链接}' : ''
            },
            [VariableType.DEVICE_ID]: v => {
                return v.deviceIdVariable?.value === DEVICE_ID_VALUE ? '{设备ID}' : ''
            },
            [VariableType.SYSTEM]: v => {
                if (!v.systemVariable?.value) {
                    return ''
                }
                const { value } = v.systemVariable
                return `{常用日期/${variableSystemNameMap[value]}}`
            },
            [VariableType.ARG_VARIABLE]: v => {
                if (!upstreamOptions || !v.argVariable?.nodeId || !v.argVariable.argId) {
                    return
                }

                const path = findOptionPath([v.argVariable.nodeId, v.argVariable.argId], upstreamOptions)
                if (!path) {
                    return
                }

                return `{${path.join('/')}}`
            },
            [VariableType.UPSTREAM]: v => {
                if (!upstreamOptions || !v.upstreamVariable?.nodeId || !v.upstreamVariable.fieldId) {
                    return
                }

                const path = findOptionPath([v.upstreamVariable.nodeId, v.upstreamVariable.fieldId], upstreamOptions)
                if (!path) {
                    return
                }

                return `{${path.join('/')}}`
            },

            [VariableType.USER]: v => {
                if (!userOption || !v.userVariable?.fieldId) {
                    return
                }
                const path = findOptionPath([v.userVariable.fieldId], userOption.children)
                if (!path) {
                    return
                }
                return `{${userOption.label}/${path.join('/')}}`
            },
            [VariableType.SELECT_DATASOURCE]: v => {
                const ds = dataSourceList?.find(item => item.id === v.selectDataSourceVariable?.dsId)
                if (!ds || !v.selectDataSourceVariable?.fieldId) {
                    return
                }
                return `查询数据表：${ds.name}`
            }
        },
        () => {
            return ''
        }
    )
}

function findOptionPath(valuePath: string[], tree?: VariableTree2[]) {
    if (!tree) {
        return
    }

    function curr(options: VariableTree2[], path: string[]): string[] | undefined {
        for (const option of options) {
            if (option.value === path[0]) {
                if (option.children) {
                    const res = curr(option.children, path.slice(1))
                    if (res && res.length > 0) {
                        return [option.label, ...res]
                    }
                }
                return [option.label]
            }
        }
    }

    return curr(tree, valuePath)
}

const extractSubObject = (obj: RichTextContentProtocol) => {
    for (const key in obj) {
        if (key !== 'type') {
            return obj[key]
        }
    }
    return null
}

export const extractDataFn = (data: RichTextContentProtocol | undefined) => {
    if (!data) {
        return {
            nodeIds: [],
            objects: []
        }
    }
    const nodeIds: string[] = []
    const objects: (RichTextOfTextNode | VariableADTvalue)[] = []

    function traverseContent(content: RichTextContentProtocol) {
        if (Array.isArray(content)) {
            content.forEach(item => {
                if (item.type === 'text') {
                    objects.push({ type: 'text', value: item.text })
                } else if (item.type === 'variable' && item.attrs.value) {
                    objects.push(item.attrs.value)

                    const subObject = extractSubObject(item.attrs.value)
                    if (subObject) {
                        const { nodeId } = subObject
                        if (nodeId && !nodeIds.includes(nodeId)) {
                            nodeIds.push(nodeId)
                        }
                    }
                }
                if (item.content) {
                    traverseContent(item.content)
                }
            })
        }
    }

    if (data.type === 'doc' && data.content) {
        traverseContent(data.content)
    }

    return {
        nodeIds,
        objects
    }
}
